/**
 * Dialog allows you to assign a list of device ids, separated by spaces or
 * other non-device-id-like characters, to a device group. By default, neither
 * the device ids nor the device group is set, but you can pre-populate these
 * by providing options when instantiating this dialog. The properties of the
 * options object are as follows:
 *
 *      device_ids: an array of device ids;
 *      selected_devicegroup_type: a string devicegroup type,
 *          eg. 'development_devicegroup'. This will freeze the dialog on this
 *          device group type. Only devicegroups of this type will be visible
 *          in the menu;
 *      selected_devicegroup: a device group pre-selected. This will freeze the
 *          dialog on this device group. Since Product is implied by the
 *          devicegroup, the Product menu will also be frozen;
 *      selected_product: a Product pre-selected. This will freeze the product
 *          menu on this product. Only devicegroups from this product will
 *          be selectable from the devicegroup menu.
 *      omitted_types: an Array of devicegroup types to omit from the
 *          devicegroups_types popup
 *
 *
 * Note that there's also an "assign_devices" dialog. That's different. This is
 * the dialog that has a dropdown for product and workspace, and allows you to
 * paste in a comma-separated list of device IDs.
 */
import _ from 'underscore';
import Backbone from 'backbone';
import Radio from 'backbone.radio';
import Marionette from 'backbone.marionette';
import Handlebars from 'handlebars';
import DialogInterface from '@/interfaces/dialog';
import alert_mixin from '@/mixins/alert';
import PaginatedMenuView from '@/views/paginated_menu_view';
import ResponseResultsView from '@/views/response_results_view';
import move_devices_template from '@/../templates/move-devices-dialog';
import deployment_info_template from '@/../templates/move-devices-devicegroup-info';
import submitted_template from '@/../templates/move-devices-dialog-submitted';

var globalCh = Radio.channel('global');
var apiCh = Radio.channel('api');

/**
 * Return a string of device ids from either a string or an array
 */
function stringify_device_ids(device_ids) {
    if (_.isString(device_ids)) { return device_ids; }
    if (_.isArray(device_ids)) { return device_ids.join(' '); }
    return '';
}

function get_dg_wording(_internal) {
    return 'Device Group';
}

var dg_type_map = {
    development_devicegroup: 'Development Device Group',
    pre_factoryfixture_devicegroup: 'Test Fixture Device Group',
    pre_production_devicegroup: 'Test Production Device Group',
    production_devicegroup: 'Production Device Group',
    factoryfixture_devicegroup: 'Fixture Device Group'
};

var view = Marionette.View
    .extend(DialogInterface)
    .extend(alert_mixin)
    .extend({
    template: Handlebars.compile(move_devices_template),
    ui: {
        form: 'form',
        panel: '.device-devicegroup-panel',
        devices: 'textarea[name=devices]',
        cancel: 'button[type=button].cancel',
        submit: 'button[type=submit]',
        loader: '.loader',
        accounts: '.accounts-menu',
        products: '.products-menu',
        devicegroup_types: '.devicegroup-types-menu',
        devicegroups: '.devicegroups-menu',
        devicegroup: '.destination-devicegroup .panel-body',
        alert: '.modal-alert-container .alert',
        response: '.response',
        to_code_editor: '.to-code-editor'
    },
    regions: {
        accounts: '@ui.accounts',
        products: '@ui.products',
        devicegroup_types: '@ui.devicegroup_types',
        devicegroups: '@ui.devicegroups',
        devicegroup: '@ui.devicegroup',
        alerts: '@ui.alert',
        response: '@ui.response'
    },
    events: {
        'submit @ui.form': 'on_submit',
        'click @ui.cancel': 'on_cancel',
        'keyup @ui.devices': 'on_devices_change',
        'click @ui.to_code_editor': 'to_code_editor',
    },
    initialize: function() {
        var options = this.options || {};
        // Maintain an internal Model because we need to do several
        // things when these properties change
        this._internal = new Backbone.Model({
            device_ids: stringify_device_ids(options.device_ids),
            selected_devicegroup_type:
                (
                    options.selected_devicegroup
                    && options.selected_devicegroup.type
                )
                || options.selected_devicegroup_type
                || null,
            selected_devicegroup:
                options.selected_devicegroup
                || null,
            selected_product:
                (
                    options.selected_devicegroup
                    && options.selected_devicegroup.rel.get('product')
                )
                || options.selected_product
                || null,
            selected_account:
                (
                    options.selected_devicegroup
                    && options.selected_devicegroup.rel.get('owner')
                )
                || options.selected_account
                || globalCh.request('active_account')
                || null,
            // Disable account dropdown if product and devicegroup are not null on init and we have a type. This is only for production and pre production devicegroups.
            //
            //
            // !!foo forces a boolean result from somthing
            // foo = undefined ... !!foo returns false
            // foo = 'string' ... !!foo returns true
            // foo = '' ... !!foo returns false
            // foo = -1 ... !!foo returns true
            // foo = 0 .. !!foo returns false
            lock_account:
                (!!options.selected_product && !!options.selected_devicegroup && options.selected_devicegroup.type === 'production_devicegroup') ||
                (!!options.selected_product && !!options.selected_devicegroup && options.selected_devicegroup.type === 'pre_production_devicegroup'),
            lock_devicegroup_type:
                (!!options.selected_product && !!options.selected_devicegroup && options.selected_devicegroup.type === 'production_devicegroup') || 
                (!!options.selected_product && !!options.selected_devicegroup && options.selected_devicegroup.type === 'pre_production_devicegroup'),
            omitted_types: options.omitted_types || []
        });
        this.listenTo(this._internal,
                        'change:selected_account',
                        this.on_change_selected_account);
        this.listenTo(this._internal,
                        'change:selected_product',
                        this.on_change_selected_product);
        this.listenTo(this._internal,
                        'change:selected_devicegroup_type',
                        this.on_change_selected_devicegroup_type);
        this.listenTo(this._internal,
                        'change:selected_devicegroup',
                        this.on_change_selected_devicegroup);

        // These functions will be used inside some secondary views
        // and we'll want to bind them to this view right here.
        _.bindAll(
            this,
            'change_selected_product',
            'change_selected_devicegroup_type',
            'change_selected_devicegroup',
            'change_selected_account'
        );
        
        // Must call this so dialogs close and reopen properly
        this.destroyOnClose();
    },
    templateContext: function() {
        var show_regions = Radio.request('api', 'cloud_regions') && Radio.request('api', 'cloud_regions').length;
        var device_help_text = 
            (this._internal.get('selected_devicegroup_type') === 'production_devicegroup') && show_regions
                ? 'Production Devices can only be assigned to Production Device Groups in the same Region.' 
                : '';
        return {
            device_help_text : device_help_text,
            device_ids: this._internal.get('device_ids'),
            can_move: this._internal.get('selected_devicegroup'),
            dg_wording: get_dg_wording(this._internal)
        };
    },
    on_submit: function(e) {
        var values = _.compact(this.ui.devices.val().split(/[\s,]+/));
        var devicegroup = this._internal.get('selected_devicegroup');
        var self = this;

        self.hideAlert();

        this.ui.submit.hide();
        this.ui.cancel.hide();
        this.ui.loader.addClass('loading');

        devicegroup.bulk_assign_devices(values, {
            success: function() {
                // success means there is no response
                self.show_success(values);
                if (self.options.cb && self.options.cb.success){
                    self.options.cb.success(values);
                }
            },
            error: function(resp) {
                self.show_error(resp, values);
                if (self.options.cb && self.options.cb.error){
                        self.options.cb.error(resp);
                }
            }
        });
        e.preventDefault();
    },
    on_cancel: function() {
        this.closeDialog();
    },
    on_devices_change: function(e) {
        this.set_submit_button_state();
    },
    change_selected_account: function (model) {
        this._internal.set('selected_account', model);
    },
    change_selected_product: function (model) {
        this._internal.set('selected_product', model);
    },
    change_selected_devicegroup_type: function (model) {
        if (model) {
            this._internal.set('selected_devicegroup_type', model);
            return this._internal.get('selected_devicegroup_type');
        } else {
            return false;
        }
    },
    change_selected_devicegroup: function (model) {
        this._internal.set('selected_devicegroup', model);
    },
    show_selected_devicegroup: function() {
        var model = this._internal.get('selected_devicegroup');
        var view;
        var template = Handlebars.compile(deployment_info_template);
        var show_regions = Radio.request('api', 'cloud_regions') && Radio.request('api', 'cloud_regions').length;
        var region;
        if (show_regions) {
            region = model && model.get('region');
            region = (region === null) ? 'Default' : region;
        }

        if (model) {
            model.fetch({ data: { 'include': 'current_deployment'}}).then(
                function() {
                    view = new Marionette.View({
                        model: model,
                        template: template,
                        templateContext: function() {
                            var deployment = model.rel.get('current_deployment');
                            return {
                                region: model.type && model.type === 'production_devicegroup' && region,
                                deployment: deployment ? deployment.toJSON() : null,
                                product: model.rel.get('product').toJSON(),
                                is_production_devicegroup:
                                    model.type.indexOf('production_devicegroup') > -1
                            };
                        }
                    });
                    this.getRegion('devicegroup').show(view);
                }.bind(this),
                function(err) {
                    console.warn(err);
                }
            );

        } else {
            // Build a really simple view to show the 'current'
            // information, really just a message saying that there's
            // no selected device group.
            view = new Marionette.View({
                template: template
            });
            view.render = function render(){
                var data = {
                    deployment: null,
                    product: null
                };
                var template = this.getTemplate();
                var html = Marionette.Renderer.render(template, data, this);
                this.$el.html(html);
            };
            this.getRegion('devicegroup').show(view);
        }
    },

    // New Show Device Group Type Menu
    show_devicegroup_types: function() {
        var apiCh = Radio.channel('api');
        var groups = apiCh.request('development_devicegroup');
        var _internal = this._internal;
        var dialog = this;
        var region = this.getRegion('devicegroup_types');
        let selected_devicegroup_type = _internal.get('selected_devicegroup_type');

        // Create a list of objects { id: id, name: human_name }
        // for the devicegroup types we want to show in the menu
        var all_types = _.chain(groups.development_types)
            // Don't include a type if it's among the omitted
            // types we initialized the dialog with
            .filter(function(type) {
                return _internal.get('omitted_types').indexOf(type) == -1;
            })
            // Make a human-readable name for each type destined for the menu
            .map(function(type){
                return { id: type, name: dg_type_map[type] };
            })
            .value();
        let types = all_types.filter(t => t.name);

        let title = 'Choose a '+ get_dg_wording(_internal) +' Type...';

        if (_internal.get('selected_devicegroup_type')) {
            selected_devicegroup_type = _internal.get('selected_devicegroup_type');
            // UI Passes a string of the devicegroup type, otherwise we have a model
            // a model will have a cid property, if not found, convert to a model
            if ( !selected_devicegroup_type.cid ) {
                selected_devicegroup_type = new Backbone.Model({ id: selected_devicegroup_type, name: dg_type_map[selected_devicegroup_type] });
            }
            title = selected_devicegroup_type.get('name');
        }
        let view_opts = {
            title: title,
            collection: new Backbone.Collection(types),
            childView: Marionette.View,
            disabled: this._internal.get('lock_devicegroup_type') || !this._internal.get('selected_product'),
            no_link: true
        };

        let view = new PaginatedMenuView(view_opts);
        view.on('select', dialog.change_selected_devicegroup_type);
        region.show(view);
        if ( _internal.get('lock_devicegroup_type') ) {
            this.change_selected_devicegroup_type(selected_devicegroup_type);
        }
 },

    // End Show Device Group Type Menu
    show_devicegroup_menu: function() {
        var groups = apiCh.request('development_devicegroup');
        var _internal = this._internal;
        var selected_product = _internal.get('selected_product');
        var selected_product_id = selected_product && selected_product.id
                                || null;
        var selected_devicegroup = _internal.get('selected_devicegroup');
        var selected_devicegroup_type = _internal.get('selected_devicegroup_type');
        if (  selected_devicegroup_type && selected_devicegroup_type.id ) selected_devicegroup_type = selected_devicegroup_type.id;
        if ( selected_devicegroup ) {
            selected_devicegroup_type = selected_devicegroup.type;
        }
        var dialog = this;
        var region = this.getRegion('devicegroups');
        var disabled = this._internal.get('lock_devicegroup')
                    || !(
                            this._internal.get('selected_product')
                        && this._internal.get('selected_devicegroup_type')
                        )
        ;
        var view;

        var view_opts = {
            title: 'Choose a ' + get_dg_wording(this._internal) + '...',
            collection: groups,
            filter: function(child) {
                var product = child.rel.get('product');
                if (!selected_product) return false;
                if (!selected_devicegroup_type && !selected_devicegroup) {
                    return false;
                }
                return (
                    product
                    && product.id == selected_product.id
                    && child.type == selected_devicegroup_type
                );
            },
            childView: Marionette.View,
            no_link: true
        };
        if (selected_product_id && selected_devicegroup_type) {
            groups.fetch({
                data: {
                    'filter[product.id]': selected_product_id,
                    'filter[type]': selected_devicegroup_type

                },
                success: function(coll, resp, opts) {
                    view_opts.next_url = resp.links.next;
                    view_opts.prev_url = resp.links.prev;
                    view = new PaginatedMenuView(view_opts);

                    view.on('select', dialog.change_selected_devicegroup);
                    region.show(view);
                    view.disable(disabled);
                    if (selected_devicegroup) {
                        view.set_menu_title(selected_devicegroup.get('name'));
                    }
                }
            });
        } else {
            view_opts.filter = function() { return false; };
            view = new PaginatedMenuView(view_opts);
            region.show(view);
            view.disable(disabled);
        }
    },
    show_product_menu: function() {
        var products = apiCh.request('product');
        var _internal = this._internal;
        var dialog = this;
        var region = this.getRegion('products');
        var selected_product = this._internal.get('selected_product');
        var active = _internal.get('selected_account') || globalCh.request('active_account');
        var view_opts = {
            title: 'Choose a Product...',
            filter: function(item) {
                var owner = item.rel.get('owner');
                // Only show products owned by the active account
                return (owner && owner.id === active.id);
            },
            collection: products,
            childView: Marionette.View,
            model: selected_product,
            no_link: true
        };
        products.fetch({
            data: {
                'filter[owner.id]': active.id
            },
            success: function(coll, resp, opts) {
                view_opts.next_url = resp.links.next;
                view_opts.prev_url = resp.links.prev;

                var view = new PaginatedMenuView(view_opts);
                view.on('select', function(model){
                    if (model) {
                        dialog.change_selected_product(model);
                    }
                });

                region.show(view);

                if (selected_product) {
                    view.set_menu_title(selected_product.get('name'));
                }
            }
        });
    },
    show_account_menu: function() {
        var primary = globalCh.request('primary_account');
        var _internal = this._internal;
        var dialog = this;
        var disabled = this._internal.get('lock_account');
        var region = this.getRegion('accounts');
        var selected_account = _internal.get('selected_account');
        var accounts = new Backbone.Collection();
        _.each(primary.proxies.models, function(a) { accounts.add(a); });
        accounts.add(primary);

        var view_opts = {
            title: 'Choose an Account...',
            collection: accounts,
            childView: Marionette.View,
            next_url: undefined,
            prev_url: undefined,
            no_link: true
        };

        if (selected_account) {
            view_opts.title = selected_account.get('username');
        }

        var view = new PaginatedMenuView(view_opts);

        view.on('select', function(model){
            if (model) {
                dialog.change_selected_account(model);
            }
        });
        region.show(view);
        view.disable(disabled);


        this.listenTo( selected_account, 'change', function(){
           view.set_menu_title(selected_account.get('username'));
        });
    },
    /**
     * Put dialog into 'success' state. This modifies the DOM. From
     * here the dialog doesn't do anything else so if you need to
     * do more device moving, you need to close the dialog box
     * and create a new one.
     *
     * @param Array a list of devices { id: id, type: 'device' }
     */
    to_code_editor: function() {
        var devicegroup = this._internal.get('selected_devicegroup');
        var device = this._internal.get('device_ids');
        globalCh.request('showCodeEditor', devicegroup.draft, devicegroup, device);
    },
    show_success: function(devices) {
        this.ui.submit.remove();
        this.ui.loader.remove();
        var devicegroup = this._internal.get('selected_devicegroup');
        var selected_devicegroup_type = devicegroup.type;
        var deployment = devicegroup.rel.get('current_deployment');

        var data = {
            devices_count: devices.length,
            devicegroup_name: devicegroup.get('name'),
            devicegroup_type: devicegroup.type_readable(),
            deployment_sha: deployment ? deployment.get('sha') : false,
            product_name: devicegroup.rel.get('product').get('name'),
            account_name: devicegroup.rel.get('owner').get('username'),
            deployment_date: deployment? deployment.get('created_at') : false
        };
        var tmpl = Handlebars.compile(submitted_template);
        var view = Backbone.Marionette.View.extend({
            template: tmpl,
            templateContext: function(){
                return this.options.data;
            }
        });
        this.ui.panel.hide();
        this.getRegion('response').show( new view({ data: data }));

        if (selected_devicegroup_type === "development_devicegroup") {
            return  $(".to-code-editor").show();
        }
        if (selected_devicegroup_type === "pre_factoryfixture_devicegroup") {
                return  $(".to-code-editor").show();
        }
        if (selected_devicegroup_type === "pre_production_devicegroup") {
            return  $(".to-code-editor").show();
        }

        var devices_string =
            devices.length === 1 ? 'device was' : 'devices were';
        var devices_succeeded = devices.length;
        this.alert([
            devices_succeeded,
            devices_string,
            'moved successfully to',
            devicegroup.get('name'),
            devicegroup.get('type')
        ].join(' '), 'success', 0);

        devicegroup.trigger('change:devices', devices);
    },
    /**
     * If we get an error, there might have still been some success.
     */
    show_error: function(resp, values){
        this.ui.submit.remove();
        this.ui.loader.remove();

        var devicegroup = this._internal.get('selected_devicegroup');

        this.ui.panel.hide();

        this.getRegion('response').show(
            new ResponseResultsView({resp: resp, values: values})
        );

        if ( resp.responseJSON.meta ) {
            var devices_succeeded = resp.responseJSON.meta.succeeded || 0;
            if ( devices_succeeded > 0 ){
                var succeeded_string =
                    devices_succeeded === 1 ? 'device was' : 'devices are';
                this.alert([
                    devices_succeeded,
                    succeeded_string,
                    'moved successfully to',
                    devicegroup.get('name'), devicegroup.type
                ].join(' '), 'success', 0);
            }

            var devices_failed =
                    resp.responseJSON.meta.total
                - resp.responseJSON.meta.succeeded;
            var failed_string = devices_failed === 1 ? 'device' : 'devices';
            this.alert([
                devices_failed,
                failed_string,
                'failed to move to',
                devicegroup.get('name'),
                devicegroup.type
            ].join(' '), 'danger', 0);
        }
    },
    // All menus in this dialog must have a selection
    // in order to enable the submit button
    set_submit_button_state: function() {
        var groups = apiCh.request('development_devicegroup');

        // There must be some device ids in the textarea
        var device_ids_has_content = this.ui.devices.val().trim();

        // All menus must have something selected
        var all_menus_have_selection = _.reduce(
            // Take these keys and put their values in an array...
            _.map([
                'selected_devicegroup_type',
                'selected_devicegroup',
                'selected_product'
            ], _.bind(function(attr) {
                return this._internal.get(attr);
            }, this)),
            // ...then reduce that array down to a boolean, true only
            // if all of the items in the array are non-falsy
            function(memo, attr) {
                return memo && attr;
            },
            true
        );

        // Production devicegroups must have a deployment
        var not_production_group_without_deployment = true;
        var group = this._internal.get('selected_devicegroup');
        var deployment;
        if (group
            && group.type
            && group.type === groups.TYPES_MAP.production) {
            deployment = group.rel.get('current_deployment');
            if (!deployment) {
                not_production_group_without_deployment = false;
            }
        }

        var disable_button = !(
                all_menus_have_selection
                && device_ids_has_content
                && not_production_group_without_deployment
        );

        this.ui.submit.prop('disabled', disable_button);
    },
    on_change_selected_account: function(model) {
        this.change_selected_devicegroup(null);
        this.change_selected_product(null);
        this.show_product_menu();
        this.show_devicegroup_types();
        this.show_devicegroup_menu();
        this.set_submit_button_state();
    },
    on_change_selected_product: function(model) {
        this.change_selected_devicegroup(null);
        this.show_devicegroup_types();
        this.show_devicegroup_menu();
        this.set_submit_button_state();
    },
    on_change_selected_devicegroup_type: function (model) {
        this.change_selected_devicegroup(null);
        this.show_devicegroup_types();
        this.show_devicegroup_menu();
        this.set_submit_button_state();
    },
    on_change_selected_devicegroup: function (model) {
        this.show_selected_devicegroup();
        this.show_devicegroup_types();
        this.update_submit_button(model);
        this.set_submit_button_state();
    },
    update_submit_button: function(model) {
        this.ui.submit.prop('disabled', !model);
    },
    onAttach: function() {
        DialogInterface.onAttach.call(this);
    },
    onBeforeAttach: function() {
        this.show_product_menu();
        this.show_account_menu();
        this.show_devicegroup_types();
        this.show_devicegroup_menu();
        this.show_selected_devicegroup();
        this.set_submit_button_state();
    }
});

export default view;

